import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { addShift, updateShift, deleteShift } from "../../services/ScheduleService";

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${({ theme }) => theme.zIndex.overlay};
`;

const Modal = styled.div`
    background: ${({ theme }) => theme.colors.card};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    padding: 2rem;
    width: 100%;
    max-width: 420px;
    box-shadow: ${({ theme }) => theme.shadows.lg};
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const Title = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.colors.text};
    font-size: 1.2rem;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
`;

const Label = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
    opacity: 0.7;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const Input = styled.input`
    padding: 0.6rem 0.8rem;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: 0.95rem;
    &:disabled {
        opacity: 0.5;
    }
`;

const TextArea = styled.textarea`
    padding: 0.6rem 0.8rem;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    border: 1px solid ${({ theme }) => theme.colors.lineColor};
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: 0.95rem;
    resize: vertical;
    min-height: 70px;
`;

const ButtonRow = styled.div`
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    margin-top: 0.5rem;
`;

const Btn = styled.button`
    padding: 0.55rem 1.2rem;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    font-family: ${({ theme }) => theme.typography.fontFamily};
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    background: ${({ variant, theme }) =>
        variant === "danger" ? theme.status.error :
        variant === "primary" ? theme.colors.primary :
        theme.colors.buttonBackground};
    color: ${({ variant, theme }) =>
        variant ? "#fff" : theme.colors.buttonText};
    opacity: ${({ disabled }) => disabled ? 0.6 : 1};
    &:hover:not(:disabled) { opacity: 0.85; }
`;

const ErrorMsg = styled.p`
    color: ${({ theme }) => theme.status.error};
    font-size: 0.85rem;
    margin: 0;
`;

function ShiftModal({ date, employeeId, employeeName, existingShift, onClose, onSaved }) {
    const isEdit = !!existingShift;

    const [startTime, setStartTime] = useState(existingShift?.startTime?.slice(0, 5) || "08:00");
    const [endTime, setEndTime] = useState(existingShift?.endTime?.slice(0, 5) || "16:00");
    const [note, setNote] = useState(existingShift?.note || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSave = async () => {
        setError("");
        if (!startTime || !endTime) { setError("Unesite početak i kraj smjene"); return; }
        if (startTime >= endTime) { setError("Kraj smjene mora biti nakon početka"); return; }
        setLoading(true);
        try {
            const payload = {
                employeeId,
                date,
                startTime: startTime + ":00",
                endTime: endTime + ":00",
                note: note || null,
            };
            if (isEdit) {
                await updateShift(existingShift.id, payload);
            } else {
                await addShift(payload);
            }
            onSaved();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await deleteShift(existingShift.id);
            onSaved();
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <Title>{isEdit ? "Uredi smjenu" : "Dodaj smjenu"}</Title>

                <Field>
                    <Label>Zaposlenik</Label>
                    <Input value={employeeName} disabled />
                </Field>

                <Field>
                    <Label>Datum</Label>
                    <Input value={date} disabled />
                </Field>

                <Field>
                    <Label>Početak</Label>
                    <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                </Field>

                <Field>
                    <Label>Kraj</Label>
                    <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                </Field>

                <Field>
                    <Label>Napomena (opcionalno)</Label>
                    <TextArea value={note} onChange={(e) => setNote(e.target.value)} placeholder="npr. dežurstvo, zamjena..." />
                </Field>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <ButtonRow>
                    {isEdit && (
                        <Btn variant="danger" onClick={handleDelete} disabled={loading}>
                            Obriši
                        </Btn>
                    )}
                    <Btn onClick={onClose} disabled={loading}>Odustani</Btn>
                    <Btn variant="primary" onClick={handleSave} disabled={loading}>
                        {loading ? "..." : "Spremi"}
                    </Btn>
                </ButtonRow>
            </Modal>
        </Overlay>
    );
}

export default ShiftModal;
